import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { PhoneCall, Upload } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api'
import { explainError } from '@/lib/explain-error'
import { formatDateTime } from '@/lib/utils'
import { useUsers } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import type { CallImportPreview, CallImportResult } from '@/types'

const NO_USER = '__none__'

function talk(seconds: number) {
  const m = Math.round(seconds / 60)
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

/** Picks the GRM user a phone-system sub-account belongs to. */
function UserPicker({
  id,
  account,
  value,
  onChange,
  users,
}: {
  id: string
  account: string
  value: string | null
  onChange: (v: string | null) => void
  users: { id: string; name: string }[]
}) {
  return (
    <Select value={value ?? NO_USER} onValueChange={(v) => onChange(v === NO_USER ? null : v)}>
      <SelectTrigger id={id} className="w-64" aria-label={`GRM user for phone account ${account}`}>
        <SelectValue placeholder="Choose a user" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_USER}>Not set</SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export default function CallImport() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<CallImportPreview | null>(null)
  const [result, setResult] = useState<CallImportResult | null>(null)
  const [choices, setChoices] = useState<Record<string, string | null>>({})

  const { data: usersData } = useUsers({ limit: 10000 })
  const users = useMemo(
    () =>
      (usersData?.data ?? [])
        .filter((u) => u.is_active !== false)
        .map((u) => ({ id: u.id, name: u.name || u.email || u.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [usersData]
  )

  const accountsQuery = useQuery({
    queryKey: ['call-import', 'accounts'],
    queryFn: () => apiClient.getCallAccounts(),
  })
  const unmatchedQuery = useQuery({
    queryKey: ['call-import', 'unmatched'],
    queryFn: () => apiClient.getUnmatchedCalls(1),
  })

  const previewMutation = useMutation({
    mutationFn: (f: File) => apiClient.callImportPreview(f),
    onSuccess: (p) => {
      setPreview(p)
      setResult(null)
      setChoices(Object.fromEntries(p.unmappedAccounts.map((a) => [a, null])))
    },
    onError: (e) =>
      toast({
        title: "The file couldn't be read",
        description: explainError(e, 'Check it is a VoIP.ms call export and try again.'),
        variant: 'destructive',
      }),
  })

  const saveAccounts = useMutation({
    mutationFn: (mappings: { external_account: string; user_id: string | null }[]) =>
      apiClient.saveCallAccounts(mappings),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['call-import', 'accounts'] })
      toast({ title: 'Phone accounts saved' })
      if (file) previewMutation.mutate(file)
    },
    onError: (e) =>
      toast({
        title: "The phone accounts weren't saved",
        description: explainError(e, 'Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const commitMutation = useMutation({
    mutationFn: (f: File) => apiClient.callImportCommit(f),
    onSuccess: (r) => {
      setResult(r)
      setPreview(null)
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      queryClient.invalidateQueries({ queryKey: ['call-import'] })
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
    },
    onError: (e) =>
      toast({
        title: "The calls weren't imported",
        description: explainError(e, 'Nothing was saved. Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const rematchMutation = useMutation({
    mutationFn: () => apiClient.rematchCalls(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['call-import', 'unmatched'] })
      toast({
        title: r.relinked
          ? `${r.relinked} call${r.relinked === 1 ? '' : 's'} matched`
          : 'No new matches',
        description: r.relinked
          ? undefined
          : 'Add the number to the right contact, lead or account, then try again.',
      })
    },
    onError: (e) =>
      toast({
        title: "The calls couldn't be matched again",
        description: explainError(e, 'Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const unmapped = preview?.unmappedAccounts ?? []
  const allChosen = unmapped.every((a) => choices[a])
  const unmatchedTotal = unmatchedQuery.data?.total ?? 0

  return (
    <main className="space-y-6" aria-labelledby="call-import-title">
      <div>
        <div className="flex items-center gap-3">
          <PhoneCall className="h-7 w-7 text-primary" aria-hidden="true" />
          <h1 id="call-import-title" className="text-3xl font-bold tracking-tight">
            Call import
          </h1>
        </div>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Bring calls from the phone system (VoIP.ms) into GRM. Each call is linked to the contact,
          lead or account with that phone number, and counts as activity everywhere in GRM.
          Uploading the same calls again adds nothing.
        </p>
      </div>

      <Tabs defaultValue="import">
        <TabsList>
          <TabsTrigger value="import">Import</TabsTrigger>
          <TabsTrigger value="unmatched">
            Not matched{unmatchedTotal ? ` (${unmatchedTotal})` : ''}
          </TabsTrigger>
          <TabsTrigger value="accounts">Phone accounts</TabsTrigger>
        </TabsList>

        {/* ---------------- Import ---------------- */}
        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload a call export</CardTitle>
              <CardDescription>
                In VoIP.ms, export the call detail records (CDR) as CSV for any date range, for one
                rep or all of them. Times are read as {preview?.timeZone ?? 'Toronto time'}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="call-file">Call export (.csv)</Label>
                  <input
                    id="call-file"
                    ref={fileRef}
                    type="file"
                    accept=".csv,.txt,text/csv"
                    className="block text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onChange={(e) => {
                      setFile(e.target.files?.[0] ?? null)
                      setPreview(null)
                      setResult(null)
                    }}
                  />
                </div>
                <Button
                  onClick={() => file && previewMutation.mutate(file)}
                  disabled={!file || previewMutation.isPending}
                >
                  <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                  {previewMutation.isPending ? 'Checking…' : 'Check file'}
                </Button>
              </div>

              <div role="status" aria-live="polite">
                {result && (
                  <p className="rounded-md border bg-muted/40 p-3 text-sm">
                    Imported {result.added} call{result.added === 1 ? '' : 's'}
                    {result.skipped ? `, skipped ${result.skipped} already in GRM` : ''}.{' '}
                    {result.matched} linked to a contact, lead or account
                    {result.unmatched + result.ambiguous
                      ? `; ${result.unmatched + result.ambiguous} not matched (see the Not matched tab)`
                      : ''}
                    .
                    {result.relinked
                      ? ` ${result.relinked} earlier unmatched call${result.relinked === 1 ? ' is' : 's are'} now matched too.`
                      : ''}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {preview && (
            <Card>
              <CardHeader>
                <CardTitle>What will be imported</CardTitle>
                <CardDescription>
                  {preview.totals.calls} calls
                  {preview.from && preview.to
                    ? ` from ${preview.from.slice(0, 10)} to ${preview.to.slice(0, 10)}`
                    : ''}
                  . {preview.totals.new} new
                  {preview.totals.duplicates
                    ? `, ${preview.totals.duplicates} already in GRM and will be skipped`
                    : ''}
                  . Of the new calls, {preview.totals.matched} match a contact, lead or account,{' '}
                  {preview.totals.ambiguous} match several and {preview.totals.unmatched} match
                  nothing. Unmatched calls are still imported and listed under Not matched.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Phone account</TableHead>
                        <TableHead scope="col">GRM user</TableHead>
                        <TableHead scope="col" className="text-right">
                          Calls
                        </TableHead>
                        <TableHead scope="col" className="text-right">
                          New
                        </TableHead>
                        <TableHead scope="col" className="text-right">
                          Answered
                        </TableHead>
                        <TableHead scope="col" className="text-right">
                          Talk time
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.reps.map((r) => (
                        <TableRow key={r.account}>
                          <TableCell className="font-mono text-sm">{r.account}</TableCell>
                          <TableCell>
                            {r.userId ? (
                              r.userName
                            ) : (
                              <UserPicker
                                id={`pick-${r.account}`}
                                account={r.account}
                                value={choices[r.account] ?? null}
                                onChange={(v) => setChoices((c) => ({ ...c, [r.account]: v }))}
                                users={users}
                              />
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.calls}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.new}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.connected}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {talk(r.talkSeconds)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {unmapped.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm">
                      Choose who{' '}
                      {unmapped.length === 1 ? 'this phone account' : 'these phone accounts'} belong
                      {unmapped.length === 1 ? 's' : ''} to before importing. GRM remembers it for
                      next time.
                    </p>
                    <Button
                      variant="secondary"
                      disabled={!allChosen || saveAccounts.isPending}
                      onClick={() =>
                        saveAccounts.mutate(
                          unmapped.map((a) => ({
                            external_account: a,
                            user_id: choices[a] ?? null,
                          }))
                        )
                      }
                    >
                      {saveAccounts.isPending ? 'Saving…' : 'Save phone accounts'}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => file && commitMutation.mutate(file)}
                    disabled={!file || !preview.totals.new || commitMutation.isPending}
                  >
                    {commitMutation.isPending
                      ? 'Importing…'
                      : preview.totals.new
                        ? `Import ${preview.totals.new} call${preview.totals.new === 1 ? '' : 's'}`
                        : 'Nothing new to import'}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------------- Not matched ---------------- */}
        <TabsContent value="unmatched" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Calls not matched to GRM</CardTitle>
              <CardDescription>
                The number dialled isn't on any contact, lead or account, or it's on several
                different accounts. Add the number to the right record, then match again. These
                calls already count in the rep's call totals.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="secondary"
                onClick={() => rematchMutation.mutate()}
                disabled={rematchMutation.isPending || !unmatchedTotal}
              >
                {rematchMutation.isPending ? 'Matching…' : 'Match again'}
              </Button>
              {unmatchedQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : unmatchedQuery.isError ? (
                <p className="text-sm text-destructive">
                  {explainError(unmatchedQuery.error, "The unmatched calls couldn't be loaded.")}
                </p>
              ) : unmatchedTotal === 0 ? (
                <p className="text-sm text-muted-foreground">Every imported call is matched.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Number</TableHead>
                        <TableHead scope="col">Rep</TableHead>
                        <TableHead scope="col">When</TableHead>
                        <TableHead scope="col">Outcome</TableHead>
                        <TableHead scope="col">Why</TableHead>
                        <TableHead scope="col">
                          <span className="sr-only">Search</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(unmatchedQuery.data?.data ?? []).map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono text-sm">
                            {c.formatted_phone || c.phone_number}
                          </TableCell>
                          <TableCell>{c.owner_name ?? '–'}</TableCell>
                          <TableCell>{formatDateTime(c.started_at)}</TableCell>
                          <TableCell>
                            {c.disposition}, {talk(c.duration_seconds)}
                          </TableCell>
                          <TableCell>
                            {c.match_status === 'ambiguous' ? 'On several accounts' : 'Not in GRM'}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/search?q=${encodeURIComponent(c.phone_number.slice(-7))}`}
                              className="text-sm underline"
                            >
                              Search GRM
                              <span className="sr-only">
                                {' '}
                                for {c.formatted_phone || c.phone_number}
                              </span>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Phone accounts ---------------- */}
        <TabsContent value="accounts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Phone accounts</CardTitle>
              <CardDescription>
                Every call leaves on the company number, so GRM uses the VoIP.ms sub-account to tell
                who made it. New sub-accounts are added here the first time they appear in an
                upload.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {accountsQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : accountsQuery.isError ? (
                <p className="text-sm text-destructive">
                  {explainError(accountsQuery.error, "The phone accounts couldn't be loaded.")}
                </p>
              ) : !accountsQuery.data?.length ? (
                <p className="text-sm text-muted-foreground">
                  None yet. Upload a call export and choose who each phone account belongs to.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Phone account</TableHead>
                        <TableHead scope="col">GRM user</TableHead>
                        <TableHead scope="col" className="text-right">
                          Calls imported
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accountsQuery.data.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-mono text-sm">{a.external_account}</TableCell>
                          <TableCell>
                            <UserPicker
                              id={`acct-${a.id}`}
                              account={a.external_account}
                              value={a.user_id}
                              onChange={(v) =>
                                saveAccounts.mutate([
                                  { external_account: a.external_account, user_id: v },
                                ])
                              }
                              users={users}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{a.calls}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                Changing the user only affects calls imported from now on.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  )
}
